import React, { useCallback, useState } from 'react';
import { ExpandIcon } from 'lucide-react';
import { lookInside } from '../data/content';
import { ImageSlot } from './ImageSlot';
import { Lightbox } from './Lightbox';
import { SectionHeading } from './SectionHeading';
type LookInsideSectionProps = {
  pageImages: string[];
};

/** Real 9:16 pages of the book. Wide cards on mobile (one page at a time), tap to open full size. */
export function LookInsideSection({
  pageImages
}: LookInsideSectionProps) {
  const [active, setActive] = useState<number | null>(null);
  const close = useCallback(() => setActive(null), []);

  // Hidden until the real pages of the book are loaded in data/images.ts
  if (pageImages.length === 0) return null;
  return <section id="look-inside" aria-labelledby="look-title" className="scroll-mt-4 bg-ink py-12 md:py-20" data-id="element-127" data-mp="1k9prk60tvzvm">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-128" data-mp="3qzw96la9duqs">
        <SectionHeading id="look-title" tone="dark" eyebrow={lookInside.eyebrow} title={lookInside.title} sub={lookInside.sub} data-id="element-129" data-mp="3m12x8xamebsq" />
        <p className="mt-3 text-center text-[13px] text-white/75" data-id="element-130" data-mp="3b5i374ck20el">{lookInside.hint}</p>
      </div>

      <ul className="no-scrollbar mx-auto mt-8 flex max-w-page snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-5 px-5 pb-2 md:mt-10 md:gap-4 md:scroll-px-8 md:px-8" aria-label="Przykładowe strony" data-id="element-131" data-mp="3g41kgrg7eo0m">
        {pageImages.map((url, i) => <li key={lookInside.captions[i] ?? i} className="w-[80%] shrink-0 snap-center sm:w-[46%] md:w-[31%] lg:w-[23%]" data-id="element-132" data-mp="3tv6gljkd4y67" data-mpi={i}>
            <button type="button" onClick={() => setActive(i)} className="group block w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sun" aria-label={`Otwórz w pełnym rozmiarze: ${lookInside.captions[i]}`} data-id="element-133" data-mp="2n2bmfw4jrak">
              <div className="relative aspect-[9/16] overflow-hidden rounded-xl bg-[#1B2233] ring-1 ring-white/10 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.8)]" data-id="element-134" data-mp="29h61glqlgtau">
                <ImageSlot url={url} alt={lookInside.captions[i]} slotName={`pageImages[${i}]`} fit="contain" data-id="element-135" data-mp="26dqkcuml8bl9" />
                <span className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full bg-sun text-ink shadow-md transition-transform duration-150 ease-out group-hover:scale-105" data-id="element-136" data-mp="21fyhj5ken52a">
                  <ExpandIcon className="h-4 w-4" aria-hidden="true" data-id="element-137" data-mp="2bee919ncggcb" />
                </span>
              </div>
              <p className="mt-3 text-[14px] font-medium text-white" data-id="element-138" data-mp="2p9q3rh1i26t8">
                <span className="mr-2 font-display font-extrabold text-sun" data-id="element-139" data-mp="2kcgec1e41v5e">{String(i + 1).padStart(2, '0')}</span>
                {lookInside.captions[i]}
              </p>
            </button>
          </li>)}
      </ul>
      <p className="mx-auto mt-4 max-w-page px-5 text-center text-[13px] text-white/70 md:px-8" data-id="element-140" data-mp="2u55gu6ronyol">{lookInside.note}</p>

      <Lightbox images={pageImages} captions={lookInside.captions} index={active} onClose={close} onChange={setActive} data-id="element-141" data-mp="3418sq4cc7boq" />
    </section>;
}